/**
 * Pure helpers for the case file's "Packaging feedback" section — the paste-
 * ready rendering of a case into the sales-site complaint form (Jess's
 * "copy and paste for each section" ask). Kept out of the component so the
 * clipboard fallback and the text shapes are unit-testable.
 */
import type { DoorPackagingFeedbackSection } from '../../services/qcpInvestigations';

/** The text a section's Copy button puts on the clipboard: the value alone. */
export function sectionClipboardText(section: DoorPackagingFeedbackSection): string {
  return section.value.trim();
}

/** "Form options: a · b · c" for a blank dropdown field, so Jess can pick without leaving. */
export function choicesHint(section: DoorPackagingFeedbackSection): string | null {
  if (section.value.trim() || section.choices.length === 0) return null;
  return `Form options: ${section.choices.join(' · ')}`;
}

/** Short provenance line shown under a filled value; null when there is nothing to say. */
export function sourceHint(section: DoorPackagingFeedbackSection): string | null {
  if (!section.value.trim() || !section.source) return null;
  return `From ${section.source}`;
}

/**
 * Copy text to the clipboard. Prefers the async Clipboard API; falls back to
 * a hidden textarea + execCommand for older/insecure contexts. Resolves true
 * when the copy is believed to have succeeded.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(area);
  return ok;
}
